import React, { useState, useEffect } from "react";
import SkeletonLoader from "../ui/SkeletonLoader";
import "./KydPage.css";
import { Database, ChevronDown, ChevronUp, X } from "lucide-react";

const loadingMessages = [
  "📊 Organizing database metadata for easy exploration.",
  "☕ Grab a cup of tea — we're structuring things thoughtfully.",
  "🧠 Teaching your data how to introduce itself clearly.",
  "⚡ Finalizing details to make your data easy to explore.",
  "🚀 Almost there! Preparing a clean schema view for you.......",
];

/* ── Typewriter hook ── */
function useTypewriter(messages, typingSpeed = 38, pauseAfter = 1800) {
  const [msgIndex, setMsgIndex] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [charIndex, setCharIndex] = useState(0);
  const [pausing, setPausing] = useState(false);

  useEffect(() => {
    setDisplayed("");
    setCharIndex(0);
    setPausing(false);
  }, [msgIndex]);

  useEffect(() => {
    const full = messages[msgIndex];

    if (pausing) {
      const t = setTimeout(() => {
        setMsgIndex((i) => (i + 1) % messages.length);
      }, pauseAfter);
      return () => clearTimeout(t);
    }

    if (charIndex < full.length) {
      const t = setTimeout(() => {
        setDisplayed(full.slice(0, charIndex + 1));
        setCharIndex((c) => c + 1);
      }, typingSpeed);
      return () => clearTimeout(t);
    } else {
      setPausing(true);
    }
  }, [charIndex, pausing, msgIndex, messages, typingSpeed, pauseAfter]);

  return displayed;
}

const KydPage = ({ kydData, loading, onClose }) => {
  const [openTables, setOpenTables] = useState({});
  const typedMessage = useTypewriter(loadingMessages);

  const toggle = (name) => {
    setOpenTables((p) => ({ ...p, [name]: !p[name] }));
  };

  return (
    <div className="kyd-root">
      {/* Header */}
      <div className="kyd-header">
        <div className="kyd-title">
          <Database size={18} />
          <div>
            <h2>Know Your Data</h2>
            {!loading && kydData?.ok && (
              <p>
                <strong>Database:</strong> {kydData.database_name} ·{" "}
                <strong>Tables:</strong> {kydData.tables.length}
              </p>
            )}
          </div>
        </div>

        <button className="kyd-close" onClick={onClose}>
          <X size={18} />
        </button>
      </div>

      {/* Body */}
      <div className="kyd-body">
        {loading && (
          <div className="kyd-loading">
            <div className="kyd-loading-text">
              {typedMessage}
              <span className="kyd-cursor">|</span>
            </div>

            <div className="kyd-skeleton">
              {[1, 2, 3].map((i) => (
                <div key={i} className="kyd-section">
                  <div className="kyd-section-header">
                    <SkeletonLoader width="40%" height="16px" />
                    <SkeletonLoader width="80px" height="12px" />
                  </div>

                  <div className="kyd-section-body">
                    {[1, 2, 3, 4].map((r) => (
                      <div key={r} className="kyd-skeleton-row">
                        <SkeletonLoader width="20%" />
                        <SkeletonLoader width="15%" />
                        <SkeletonLoader width="35%" />
                        <SkeletonLoader width="30%" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && !kydData?.ok && (
          <div className="kyd-state">No data available</div>
        )}

        {!loading &&
          kydData?.ok &&
          kydData.tables.map((table) => {
            const open = openTables[table.table_name];
            return (
              <div key={table.table_name} className="kyd-section">
                <button
                  className="kyd-section-header"
                  onClick={() => toggle(table.table_name)}
                >
                  <span className="table-name">{table.table_name}</span>
                  <span className="table-meta">
                    {table.columns.length} columns
                  </span>
                  {open ? <ChevronUp /> : <ChevronDown />}
                </button>

                {open && (
                  <div className="kyd-section-body">
                    {table.table_description && (
                      <div className="table-description">
                        {table.table_description}
                      </div>
                    )}

                    <div className="kyd-table-wrapper">
                      <table className="kyd-table">
                        <thead>
                          <tr>
                            <th>Column</th>
                            <th>Type</th>
                            <th>Description</th>
                            <th>Data overview</th>
                          </tr>
                        </thead>
                        <tbody>
                          {table.columns.map((c) => (
                            <tr key={c.name}>
                              <td className="col-name">{c.name}</td>
                              <td className="col-type">
                                {c.dtype}
                                {c.db_dtype && (
                                  <span className="sub-type">
                                    {c.db_dtype}
                                  </span>
                                )}
                              </td>
                              <td className="col-desc">
                                {c.description || "—"}
                              </td>
                              <td className="col-overview">
                                {c.range
                                  ? c.range
                                  : c.samples?.length
                                  ? c.samples.slice(0, 4).join(", ") +
                                    (c.samples.length > 4 ? " …" : "")
                                  : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default KydPage;
